import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import { submitLead } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const validateLead = (form) => {
  if (!/^[6-9]\d{9}$/.test(form.mobile)) return "Enter a valid 10-digit Indian mobile number";
  return null;
};

export const LeadForm = ({ defaultType = "personal", lender = null, compact = false, source = "website", title }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    loan_type: defaultType,
    loan_amount: "",
    monthly_income: "",
    employment_type: "salaried",
    city: "",
    full_name: "",
    email: "",
    mobile: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Prefill from logged-in user's profile
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        full_name: f.full_name || user.name || "",
        email: f.email || user.email || "",
        mobile: f.mobile || user.mobile || "",
        city: f.city || user.city || "",
        monthly_income: f.monthly_income || (user.monthly_salary ? String(user.monthly_salary) : ""),
        employment_type: user.employment_type || f.employment_type,
      }));
    }
  }, [user]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    const error = validateLead(form);
    if (error) {
      toast.error(error);
      return;
    }
    setLoading(true);
    try {
      await submitLead({
        ...form,
        loan_amount: form.loan_amount ? Number(form.loan_amount) : null,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        lender,
        source,
      });
      setSuccess(true);
      toast.success("Application received! Our advisor will call you shortly.");
    } catch (e) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="lead-success" className="bg-[#EAF5FD] border border-[#1088D4]/30 rounded-xl p-8 text-center">
        <CheckCircle2 className="mx-auto text-[#16A34A]" size={48}/>
        <h3 className="mt-4 text-xl font-semibold text-[#273B59] font-heading">Application Received</h3>
        <p className="mt-2 text-sm text-[#475569]">Thank you, {form.full_name || "there"}. Our loan advisor will reach out to {form.mobile} within 24 hours with the best offers.</p>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={onSubmit} className={`bg-white border border-[#E2E8F0] rounded-2xl ${compact ? "p-5" : "p-6 sm:p-8"} shadow-sm`}>
      {title && <h3 className="text-lg sm:text-xl font-bold text-[#273B59] font-heading mb-1">{title}</h3>}
      {title && <p className="text-xs text-[#475569] mb-5">Get matched with the right lender in minutes.</p>}

      <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"} gap-3`}>
        <div>
          <label className="text-xs font-medium text-[#475569] mb-1 block">Loan Type</label>
          <Select value={form.loan_type} onValueChange={upd("loan_type")}>
            <SelectTrigger data-testid="lead-loan-type" className="bg-white"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="personal">Personal Loan</SelectItem>
              <SelectItem value="business">Business Loan</SelectItem>
              <SelectItem value="overdraft">Flexi Overdraft</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-[#475569] mb-1 block">Loan Amount (₹)</label>
          <Input data-testid="lead-amount" type="number" placeholder="e.g. 500000" value={form.loan_amount} onChange={upd("loan_amount")}/>
        </div>
        <div>
          <label className="text-xs font-medium text-[#475569] mb-1 block">Monthly Income (₹)</label>
          <Input data-testid="lead-income" type="number" placeholder="e.g. 50000" value={form.monthly_income} onChange={upd("monthly_income")}/>
        </div>
        <div>
          <label className="text-xs font-medium text-[#475569] mb-1 block">Employment</label>
          <Select value={form.employment_type} onValueChange={upd("employment_type")}>
            <SelectTrigger data-testid="lead-employment" className="bg-white"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="salaried">Salaried</SelectItem>
              <SelectItem value="self-employed">Self-Employed</SelectItem>
              <SelectItem value="business">Business Owner</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-[#475569] mb-1 block">City</label>
          <Input data-testid="lead-city" placeholder="e.g. Mumbai" value={form.city} onChange={upd("city")}/>
        </div>
        <div>
          <label className="text-xs font-medium text-[#475569] mb-1 block">Full Name</label>
          <Input data-testid="lead-name" placeholder="Full name" value={form.full_name} onChange={upd("full_name")}/>
        </div>
        <div className={compact ? "" : "sm:col-span-2"}>
          <label className="text-xs font-medium text-[#475569] mb-1 block">Mobile Number *</label>
          <Input data-testid="lead-mobile" type="tel" required placeholder="10-digit mobile" value={form.mobile} onChange={upd("mobile")} maxLength={10}/>
        </div>
      </div>

      <Button data-testid="lead-submit" disabled={loading} type="submit" className="w-full mt-5 ol-btn-primary h-11 text-base">
        {loading ? <Loader2 className="animate-spin mr-2" size={16}/> : null}
        {loading ? "Submitting..." : "Get Best Offers"}
      </Button>
      <p className="mt-3 text-[10px] text-[#94A3B8] text-center">By submitting, you agree to receive calls/SMS from our advisors. Soft enquiry — no impact on your CIBIL.</p>
    </form>
  );
};
